// In src/app/wallet-test/page.tsx

"use client";

import React from "react";
import { useAccount, useConfig, useConnect } from "wagmi";
import { useAppKit } from "@reown/appkit/react";

// Import your custom hook to test it directly
import { Web3ModalProvider, useWallet } from '@/hooks/Web3';

// AppKit's pre-built button component is now typically used as a custom element,
// but for a pure React test, we will create a button that calls the hook.
// If you have a component library providing a button, you can use that for Scenario 1.

const WalletRedirectTestPageContent = () => {
  // --- Standard Hooks for Displaying Status ---
  const { address, isConnected, status } = useAccount();

  // --- Hooks for Test Scenarios ---
  const { open: openWithAppKitHook } = useAppKit();
  const { connect: connectWithCustomHook } = useWallet();
  const wagmiConfig = useConfig();
  const { connect } = useConnect();

  // --- Scenario 4: Simulate the old, problematic deep-linking ---
  const simulateOldDeepLink = () => {
    alert(
      "This simulates the old useWalletDetection.ts logic. It will try to force a redirect."
    );
    try {
        const dappUrl = encodeURIComponent(window.location.host);
        // This is the aggressive redirect link that often fails or conflicts with WalletConnect
        window.location.href = `https://metamask.app.link/dapp/${dappUrl}`;
    } catch (error) {
        console.error("Deep link failed:", error);
        alert("Deep link failed. This is expected in many desktop environments.");
    }
  };

  // --- Scenario 5: Connect directly to a specific wallet ---
  const connectDirectlyToMetaMask = () => {
    try {
      // Find the MetaMask connector from the wagmi config
      const metaMaskConnector = wagmiConfig.connectors.find(
        (c) => c.id === 'io.metamask' || c.name === 'MetaMask'
      );

      if (!metaMaskConnector) {
        alert("MetaMask connector not found in your wagmi config.");
        return;
      }

      // Use the 'connect' function from useConnect with the specific connector
      connect({ connector: metaMaskConnector });
    } catch (error) {
      console.error("Failed to connect directly to MetaMask", error);
      alert("Could not connect directly. Check the console for errors.");
    }
  };

  return (
    <div
      style={{
        fontFamily: "sans-serif",
        padding: "2rem",
        backgroundColor: "#f0f4f8",
        minHeight: "100vh",
        color: "#1a202c",
      }}
    >
      <header
        style={{
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "1rem",
          marginBottom: "2rem",
        }}
      >
        <h1 style={{ fontSize: "2rem", fontWeight: "bold" }}>
          Wallet Connection Test Page
        </h1>
        <p style={{ color: "#4a5568" }}>
          Use these scenarios to diagnose the wallet redirection issue. Test on a
          mobile device.
        </p>
      </header>

      {/* --- STATUS DISPLAY --- */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1.5rem",
          borderRadius: "8px",
          marginBottom: "2rem",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: "600",
            marginBottom: "1rem",
          }}
        >
          Current Status
        </h2>
        <p>
          <strong>Status:</strong>{" "}
          <span
            style={{
              color: isConnected ? "#38a169" : "#e53e3e",
              fontWeight: "bold",
            }}
          >
            {status}
          </span>
        </p>
        <p>
          <strong>Address:</strong>{" "}
          <span style={{ fontFamily: "monospace", color: "#2d3748" }}>
            {address ? address : "Not Connected"}
          </span>
        </p>
      </div>

      {/* --- TEST SCENARIOS --- */}
      <div style={{ display: "grid", gap: "1.5rem" }}>
        {/* Scenario 1: Standard Library Hook */}
        <div style={{ backgroundColor: "white", padding: "1.5rem", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "600" }}>
            Scenario 1: Standard `useAppKit` Hook
          </h3>
          <p style={{ color: "#718096", marginBottom: "1rem", fontSize: "0.9rem" }}>
            This calls the `open()` function directly from the library&apos;s hook. If this works, your core provider setup is correct.
          </p>
          <button
            onClick={() => openWithAppKitHook()}
            style={{
              backgroundColor: "#4299e1",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Run Test 1
          </button>
        </div>

        {/* Scenario 2: Your Custom Hook */}
        <div style={{ backgroundColor: "white", padding: "1.5rem", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "600" }}>
            Scenario 2: Your `useWallet` Hook (Mimics your app)
          </h3>
          <p style={{ color: "#718096", marginBottom: "1rem", fontSize: "0.9rem" }}>
            This button uses your exact `useWallet` hook. This is the most direct test of your application&apos;s connection flow. If Test 1 works but this fails, the issue is in your `useWallet.ts` logic.
          </p>
          <button
            onClick={connectWithCustomHook}
            style={{
              backgroundColor: "#48bb78",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Run Test 2 (Your Workflow)
          </button>
        </div>
        
        {/* Scenario 3: Direct Wallet Connection */}
        <div style={{ backgroundColor: "white", padding: "1.5rem", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "600" }}>
            Scenario 3: Connect Directly to MetaMask
          </h3>
          <p style={{ color: "#718096", marginBottom: "1rem", fontSize: "0.9rem" }}>
            This test bypasses the wallet selection screen. If this works when others fail, the issue might be with the modal&apos;s main view or WalletConnect&apos;s relay.
          </p>
          <button
            onClick={connectDirectlyToMetaMask}
            style={{
              backgroundColor: "#dd6b20",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Run Test 3
          </button>
        </div>

        {/* Scenario 4: Old Conflicting Logic */}
        <div style={{ backgroundColor: "#fee2e2", padding: "1.5rem", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#c53030" }}>
            Scenario 4: Simulate Old `useWalletDetection` Logic
          </h3>
          <p style={{ color: "#9b2c2c", marginBottom: "1rem", fontSize: "0.9rem" }}>
            This simulates the aggressive deep-link redirect from `useWalletDetection.ts`. This method often conflicts with WalletConnect and is likely the source of previous issues.
          </p>
          <button
            onClick={simulateOldDeepLink}
            style={{
              backgroundColor: "#e53e3e",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Run Conflicting Test 4
          </button>
        </div>
      </div>
    </div>
  );
};

export default function WalletRedirectTestPage() {
  return (
    <Web3ModalProvider>
      <WalletRedirectTestPageContent />
    </Web3ModalProvider>
  );
}
